import { formatTimeAgo } from "@/lib/format-time-ago";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useCallback, useEffect, useRef, useState } from "react";
import { useViewSwap } from "@/hooks/useViewSwap";
import {
  useCurrentUser,
  useMyMemberships,
  usePendingOrgs,
  usePortalReducers,
} from "@/hooks/spacetime";
import AvatarPopover from "./avatar-popover";
import { getRandomOrgAvatar } from "@/utils/utils";
import { Label } from "./ui/label";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { Check, Clock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { SmoothHeight } from "./loading-skeletons";
import { SaveButtonSpinner } from "./save-button-spinner";
import { requestOrganizationJoin } from "@/services/member.client";

const DIALOG_TITLE = "Set up your organization";
const CREATE_DIALOG_TITLE = "Create organization";
const JOIN_DIALOG_TITLE = "Join organization";
const SUCCESS_DIALOG_TITLE = "Organization created";
const SUCCESS_DESCRIPTION = "Your organization is ready.";
const REQUESTED_DIALOG_TITLE = "Your request has been sent";
const DECLINED_DIALOG_TITLE = "Your request was declined";
const PLACEHOLDER_TEXT = "Libum Credit Union";

type OrganizationDialogView =
  | "selection"
  | "create"
  | "join"
  | "success"
  | "requested"
  | "declined";

/** The organization a join request is waiting on, or was declined by. */
export type JoinRequestOrg = { id: string; name: string; logo: string };

// How long a confirmation panel is held before the dialog dismisses itself
// and hands the user over to whatever the app shows next.
const CONFIRM_HOLD_MS = 1100;
// Must outlast DialogContent's exit animation below, so the reset never
// swaps the content back to the form while the dialog is still visible.
const CONTENT_RESET_MS = 450;

// How often the "requested … ago" label re-reads the clock. The waiting view
// is a resting state — it can sit open for hours — so a label rendered once
// would go stale in place.
const REQUESTED_AT_TICK_MS = 60_000;

interface OrganizationCreateDialogProps {
  controlledOpen?: boolean;
  initialView?: OrganizationDialogView;
  isForced?: boolean;
  onSuccess?: (organizationId: string) => void;
  onJoinRequestSuccess?: (organizationId: string) => void;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  /**
   * A live join request with no active organization: the dialog holds on a
   * waiting view instead of returning to create/join. Passed by
   * `app-bootstrap`, which owns the membership rows this is derived from.
   */
  pendingRequest?: JoinRequestOrg | null;
  /** A declined request, latched by the caller (the row is already gone). */
  declinedRequest?: JoinRequestOrg | null;
  /** Withdraws the pending request. */
  onWithdrawRequest?: () => void;
  /** Clears the declined notice, releasing the caller back to create/join. */
  onDismissDeclined?: () => void;
  isWithdrawingRequest?: boolean;
}

export default function OrganizationCreateDialog({
  controlledOpen,
  initialView,
  isForced,
  onSuccess,
  onJoinRequestSuccess,
  onOpenChange,
  trigger,
  pendingRequest,
  declinedRequest,
  onWithdrawRequest,
  onDismissDeclined,
  isWithdrawingRequest = false,
}: OrganizationCreateDialogProps) {
  const { user } = useCurrentUser();
  const { pendingOrgs } = usePendingOrgs();
  const { memberships } = useMyMemberships();
  const { createOrganization, removeMember } = usePortalReducers();
  const [isWithdrawingInternal, setIsWithdrawingInternal] = useState(false);
  const withdrawInFlightRef = useRef(false);
  const getInitialView = useCallback(
    () => initialView ?? (isForced ? "selection" : "create"),
    [initialView, isForced],
  );
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(getRandomOrgAvatar());
  const [joinOrganizationId, setJoinOrganizationId] = useState("");
  const { view, isSwapping, swapTo, resetTo, renderSwap } =
    useViewSwap<OrganizationDialogView>(getInitialView);
  const [internalOpen, setInternalOpen] = useState(false);
  const [isConfirmHeld, setIsConfirmHeld] = useState(false);
  const [requestedOrgId, setRequestedOrgId] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedAsForced, setSubmittedAsForced] = useState(isForced);
  const inputRef = useRef<HTMLInputElement>(null);

  // A submit in flight, and the confirmation that follows it, outlive the
  // caller's open state on purpose: in the forced flow (`app-bootstrap`) the
  // new organization streams in and flips `controlledOpen` to false while the
  // reducer is still resolving, which would otherwise start the dialog's exit
  // animation and then re-open it on the success view — a visible flicker.
  const isOpen =
    (controlledOpen !== undefined ? controlledOpen : internalOpen) ||
    isSubmitted ||
    isConfirmHeld;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(open);
      }
      if (onOpenChange) {
        onOpenChange(open);
      }
    },
    [controlledOpen, onOpenChange],
  );

  // A caller that owns the membership rows (app-bootstrap) drives the waiting
  // views through props: the request's own state decides what is on screen,
  // and the dialog morphs to it rather than handing off to another dialog.
  // Only while the dialog is open, so a closed one doesn't animate in the dark.
  const requestView: OrganizationDialogView | null = declinedRequest
    ? "declined"
    : pendingRequest
      ? "requested"
      : null;
  const [requestedAtTick, setRequestedAtTick] = useState(() => Date.now());
  useEffect(() => {
    if (!isOpen || view !== "requested") return;

    const interval = window.setInterval(
      () => setRequestedAtTick(Date.now()),
      REQUESTED_AT_TICK_MS,
    );

    return () => window.clearInterval(interval);
  }, [isOpen, view]);

  const previousRequestViewRef = useRef<OrganizationDialogView | null>(null);
  useEffect(() => {
    if (!isOpen) return;

    const previousRequestView = previousRequestViewRef.current;
    previousRequestViewRef.current = requestView;

    if (requestView) {
      if (view !== requestView) swapTo(requestView);
      return;
    }

    // Only pull back once a request the caller was driving has gone away
    // (withdrawn, or the notice dismissed) — a request submitted here shows
    // its own waiting view a beat before the row arrives.
    if (previousRequestView && (view === "requested" || view === "declined")) {
      swapTo("selection", "back");
    }
  }, [isOpen, requestView, swapTo, view]);

  useEffect(() => {
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      const resetTimeout = window.setTimeout(() => {
        setName("");
        setJoinOrganizationId("");
        setRequestedOrgId("");
        resetTo(getInitialView());
        setAvatar(getRandomOrgAvatar());
        setIsSubmitted(false);
      }, CONTENT_RESET_MS);

      return () => window.clearTimeout(resetTimeout);
    }
  }, [getInitialView, isOpen, resetTo]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitted || (view !== "create" && view !== "join")) return;

    try {
      setSubmittedAsForced(isForced);
      setIsSubmitted(true);

      if (view === "join") {
        if (!user) {
          setIsSubmitted(false);
          return;
        }

        const requestedOrganizationId = joinOrganizationId.trim();

        if (!requestedOrganizationId) {
          setIsSubmitted(false);
          return;
        }

        await requestOrganizationJoin({
          organizationId: requestedOrganizationId,
        });

        setRequestedOrgId(requestedOrganizationId);
        swapTo("requested");

        setIsSubmitted(false);
        onJoinRequestSuccess?.(requestedOrganizationId);
        return;
      }

      await createOrganization({ name: name.trim(), logo: avatar });
      // Selection lands server-side: `create_organization` sets the caller's
      // `last_selected_organization_id`, and `my_organizations`/`my_user`
      // update live, so `useCurrentOrganization()` picks the new org up with
      // no manual fetch/refresh here. Reducers resolve `void` (SpacetimeDB
      // reducers don't return row data) — the new org's id isn't known
      // client-side, so pre-Task-5 callers that still key off `onSuccess`'s
      // argument (nav-organization.tsx) get an empty string rather than a
      // fabricated one.
      //
      // Confirm the name landed before leaving: the form swaps to a welcome
      // panel, which `isSuccessHeld` keeps on screen for SUCCESS_HOLD_MS
      // before the dialog dismisses itself (see the effect below).
      setIsConfirmHeld(true);
      swapTo("success");
    } catch (error: any) {
      showApiErrorToast(
        view === "join"
          ? "Failed to submit join request"
          : "Failed to create organization",
        error,
      );
      setIsSubmitted(false);
    }
  }, [
    avatar,
    isForced,
    isSubmitted,
    joinOrganizationId,
    name,
    createOrganization,
    swapTo,
    user,
    view,
    onJoinRequestSuccess,
  ]);

  // Kept in a ref so the beat below runs on a single timer: `onSuccess` and
  // `setIsOpen` are re-created by callers mid-beat (the forced flow flips
  // `controlledOpen` as the new org streams in), which would otherwise
  // restart the timeout on every one of those renders.
  const finishConfirmRef = useRef<() => void>(() => {});
  useEffect(() => {
    finishConfirmRef.current = () => {
      // Every latch has to drop here: each one alone holds `isOpen` true, and
      // the reset that would otherwise clear them only runs once closed.
      setIsConfirmHeld(false);
      setIsSubmitted(false);
      setIsOpen(false);
      if (view === "success") onSuccess?.("");
    };
  });

  // Closes the dialog once the welcome panel has had its beat. `onSuccess`
  // fires here rather than at reducer-resolve time so callers react to the
  // dialog actually being done, not to the write landing.
  useEffect(() => {
    if (!isConfirmHeld || view !== "success") return;

    const timeout = window.setTimeout(
      () => finishConfirmRef.current(),
      CONFIRM_HOLD_MS,
    );

    return () => window.clearTimeout(timeout);
  }, [isConfirmHeld, view]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setName(newValue);
  };

  const handleAvatarChange = (src: string) => {
    setAvatar(src);
  };

  const handleJoinOrganizationIdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setJoinOrganizationId(e.target.value);
  };

  const handleViewChange = (nextView: OrganizationDialogView) => {
    if (nextView === view || isSwapping) return;

    swapTo(nextView, nextView === "selection" ? "back" : "forward");
  };

  useEffect(() => {
    let frameId: number;

    if (isOpen && (view === "create" || view === "join")) {
      frameId = requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isOpen, view]);

  const createdName = name.trim();
  // The row for a request submitted here streams in a beat after the reducer
  // lands, so the view names the organization when it can and stays generic
  // when it can't. A caller-supplied request always wins — on a reload there
  // is no locally submitted id to match.
  const requestedOrg =
    declinedRequest ??
    pendingRequest ??
    pendingOrgs.find((org) => org.id === requestedOrgId);

  const titleFor = (v: OrganizationDialogView) =>
    v === "declined"
      ? DECLINED_DIALOG_TITLE
      : v === "requested"
        ? REQUESTED_DIALOG_TITLE
        : v === "success"
          ? createdName
            ? `Welcome to ${createdName}`
            : SUCCESS_DIALOG_TITLE
          : v === "selection"
            ? DIALOG_TITLE
            : v === "join"
              ? JOIN_DIALOG_TITLE
              : CREATE_DIALOG_TITLE;

  // The organization is named inline here rather than in the title: these two
  // read as sentences about a specific org, and a long name would wrap the
  // title awkwardly.
  // The caller's own membership row is always subscribed, so how long the
  // request has been sitting costs nothing extra to show. A declined request
  // has no row left (the org deletes it), hence no timestamp there.
  const requestedRow =
    view === "requested"
      ? memberships.find(
          (m) =>
            m.organizationId === (requestedOrg?.id ?? requestedOrgId) &&
            m.status === "requested",
        )
      : undefined;
  const requestedAgo = requestedRow
    ? formatTimeAgo(requestedRow.createdAt, requestedAtTick)
    : null;
  const withdrawing = isWithdrawingRequest || isWithdrawingInternal;
  const handleWithdrawRequest = async () => {
    if (withdrawing || withdrawInFlightRef.current) return;
    if (onWithdrawRequest) {
      onWithdrawRequest();
      return;
    }
    if (!requestedRow) return;
    withdrawInFlightRef.current = true;
    setIsWithdrawingInternal(true);
    try {
      await removeMember({
        id: requestedRow.id,
        userId: requestedRow.userId,
        organizationId: requestedRow.organizationId,
      });
      setRequestedOrgId("");
      if (isForced) swapTo("selection", "back");
      else setIsOpen(false);
    } catch (error) {
      showApiErrorToast("Failed to withdraw request", error);
    } finally {
      withdrawInFlightRef.current = false;
      setIsWithdrawingInternal(false);
    }
  };

  const orgName = (
    <b className="text-foreground">
      {requestedOrg?.name ?? "the organization"}
    </b>
  );

  const descriptionFor = (v: OrganizationDialogView): React.ReactNode =>
    v === "declined" ? (
      <>
        An admin of {orgName} declined your request to join. You can create your
        own organization, or contact an admin if you think this was a mistake.
      </>
    ) : v === "requested" ? (
      <>
        An admin of {orgName} needs to approve your request before you can
        access the organization. You&apos;ll get in automatically once they do.
      </>
    ) : v === "success" ? (
      SUCCESS_DESCRIPTION
    ) : v === "selection" ? (
      "Create a workspace or request access with an organization ID."
    ) : v === "join" ? (
      "Enter the organization ID provided by an owner or admin."
    ) : isForced ? (
      "Name your workspace and choose an organization avatar."
    ) : undefined;

  const canReturnToSelection = isForced || initialView === "selection";

  // Rendered for the live view and, mid-swap, for the outgoing one — so it
  // takes the view as an argument rather than reading `view` directly. Only
  // the live copy owns the focus ref; the outgoing one is an inert snapshot.
  const renderView = (v: OrganizationDialogView) => {
    const isLive = v === view;
    const submitLabel =
      v === "join"
        ? "Request to join"
        : (isSubmitted ? submittedAsForced : isForced)
          ? "Get started"
          : "Create organization";
    const isSubmitDisabled =
      v === "create" ? !name.trim() : !joinOrganizationId.trim();

    return (
      <div className="grid gap-4">
        <DialogHeader className="flex">
          <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
            {titleFor(v)}
          </DialogTitle>
          <DialogDescription className="text-center">
            {descriptionFor(v)}
          </DialogDescription>
        </DialogHeader>
        {v === "selection" ? (
          <Tabs
            value=""
            onValueChange={(value) =>
              handleViewChange(value as OrganizationDialogView)
            }
            className="w-full"
          >
            <TabsList className="flex h-full select-none flex-col bg-cardTabs sm:flex-row">
              <TabsTrigger
                value="create"
                className="flex h-full flex-col py-2 transition-colors hover:!bg-card hover:text-primary-foreground data-[state=active]:!bg-card"
                onClick={() => handleViewChange("create")}
              >
                <span className="font-semibold">Create</span>
                <div className="flex py-2">
                  <Image
                    src="/emojis/card-index-dividers.svg"
                    alt=""
                    width={28}
                    height={28}
                  />
                </div>
                <span className="text-xs text-muted">
                  Start a new organization
                </span>
              </TabsTrigger>
              <div
                aria-hidden="true"
                className="h-[2px] w-4/5 shrink-0 bg-card sm:h-20 sm:w-[2px]"
              />
              <TabsTrigger
                value="join"
                className="relative flex h-full flex-col py-2 transition-colors hover:!bg-card hover:text-primary-foreground data-[state=active]:!bg-card"
                onClick={() => handleViewChange("join")}
              >
                <span className="font-semibold">Join</span>
                <div className="flex py-2">
                  <Image
                    src="/emojis/locked-with-key.svg"
                    alt=""
                    width={28}
                    height={28}
                  />
                </div>
                <span className="text-xs text-muted">Request access by ID</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : v === "join" ? (
          <div className="-mt-2 space-y-3 pb-2">
            <div className="space-y-2">
              <Label htmlFor="join-org-id">Organization ID</Label>
              <Input
                ref={isLive ? inputRef : undefined}
                id="join-org-id"
                type="text"
                value={joinOrganizationId}
                className="mt-0"
                onChange={handleJoinOrganizationIdChange}
                placeholder="org_123456789"
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-form-type="other"
                spellCheck={false}
              />
            </div>
          </div>
        ) : v === "success" || v === "requested" || v === "declined" ? (
          <div className="flex flex-col items-center py-2">
            <div className="relative">
              <span className="flex size-14 items-center justify-center overflow-hidden rounded-full border-4 border-amber-400 bg-background">
                {v === "success" ? (
                  <Image
                    src={avatar}
                    alt=""
                    width={56}
                    height={56}
                    className="size-14 object-cover"
                  />
                ) : requestedOrg?.logo ? (
                  <Image
                    src={requestedOrg.logo}
                    alt=""
                    width={56}
                    height={56}
                    className="size-14 object-cover"
                  />
                ) : (
                  // Until the pending row arrives, the Join tab's own icon
                  // stands in for the organization.
                  <Image
                    src="/emojis/locked-with-key.svg"
                    alt=""
                    width={28}
                    height={28}
                  />
                )}
              </span>
              <span className="absolute -bottom-1 -right-1 grid size-7 place-items-center rounded-full bg-card">
                <span
                  className={cn(
                    "relative grid size-5 place-items-center rounded-full delay-150 duration-300 ease-out animate-in zoom-in-50 fill-mode-both",
                    v === "success"
                      ? "bg-emerald-500 dark:bg-emerald-400"
                      : v === "declined"
                        ? "bg-red-500"
                        : "bg-amber-400",
                  )}
                >
                  {v === "success" ? (
                    <Check
                      className="size-3 text-white"
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  ) : v === "declined" ? (
                    <X
                      className="size-3 text-white"
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      {/* Waiting-on-an-admin pulse: the state reads as the
                          organization's, anchored to its avatar. */}
                      <span className="absolute inline-flex size-full animate-ping rounded-full bg-amber-400 opacity-60 [animation-duration:2.6s]" />
                      <Clock
                        className="relative size-3 text-white"
                        strokeWidth={3}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </span>
              </span>
            </div>
          </div>
        ) : v === "create" ? (
          <div className="flex items-center py-2">
            <AvatarPopover
              avatar={avatar}
              onAvatarChange={handleAvatarChange}
              type="organization"
            />
            <Input
              ref={isLive ? inputRef : undefined}
              id="org-name"
              type="text"
              value={name}
              className="mt-0"
              onChange={handleInputChange}
              placeholder={PLACEHOLDER_TEXT}
              spellCheck={false}
            />
          </div>
        ) : null}
        {(v === "requested" || v === "declined") && (
          <DialogFooter
            className={cn(
              "!flex-row items-center gap-3 sm:space-x-0",
              // DialogFooter's own `sm:justify-end` outranks an unprefixed
              // override from sm up, so the note needs the variant too.
              requestedAgo
                ? "justify-between sm:justify-between"
                : "justify-end sm:justify-end",
            )}
          >
            {requestedAgo && (
              <span className="text-xs text-muted-foreground">
                Requested {requestedAgo}
              </span>
            )}
            <div className="flex items-center gap-2">
              <SaveButtonSpinner
                show={withdrawing}
                margin="right"
                collapseGap
              />
              <Button
                variant={v === "declined" ? "default" : "form"}
                onClick={
                  v === "declined" ? onDismissDeclined : handleWithdrawRequest
                }
                disabled={
                  withdrawing ||
                  (v === "requested" && !onWithdrawRequest && !requestedRow)
                }
                aria-busy={withdrawing}
              >
                {v === "declined" ? "Dismiss" : "Withdraw request"}
              </Button>
              {!isForced && v === "requested" && (
                <Button onClick={() => setIsOpen(false)} disabled={withdrawing}>
                  Done
                </Button>
              )}
            </div>
          </DialogFooter>
        )}
        {(v === "create" || v === "join") && (
          <DialogFooter className="!flex-row items-center justify-end gap-2 sm:space-x-0">
            {canReturnToSelection && (
              <Button
                type="button"
                variant="form"
                onClick={() => handleViewChange("selection")}
                disabled={isSubmitted}
              >
                Back
              </Button>
            )}
            <SaveButtonSpinner show={isSubmitted} margin="right" collapseGap />
            <Button
              disabled={isSubmitDisabled || isSubmitted}
              onClick={handleSubmit}
              aria-busy={isSubmitted}
              className="min-w-0 flex-1 sm:flex-none"
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent
        className="bg-card data-[state=closed]:duration-300"
        overlayClassName="data-[state=closed]:duration-500"
        onOpenAutoFocus={(e) => {
          if (view === "selection") {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (isForced === true || isSubmitted) e.preventDefault();
        }}
      >
        {/* Keep focus outlines inside the height animation's clipping edge. */}
        <div className="zoom-in-98 -m-1 duration-300 ease-out animate-in fade-in-75">
          <SmoothHeight className="p-1">{renderSwap(renderView)}</SmoothHeight>
        </div>
      </DialogContent>
    </Dialog>
  );
}
